/**
 * SEO Intelligence Dashboard - Consolidated DataForSEO Integration
 * Combines all DataForSEO APIs into a unified intelligence interface
 */

import React, { useState } from 'react';
import {
  Search,
  Link,
  Building2,
  Brain as BrainIcon,
  Sparkles,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  BarChart3,
  Globe,
  FileText,
  Star,
  RefreshCw as RefreshIcon,
  Target
} from 'lucide-react';
import toast from 'react-hot-toast';
import { createDataForSEOClient } from '../lib/dataforseo';

const SEOIntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'domain' | 'brand' | 'content' | 'comparison'>('domain');
  const [client] = useState(() => createDataForSEOClient());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>({});

  // Domain Analysis State
  const [domainInput, setDomainInput] = useState('');
  const [domainAnalysis, setDomainAnalysis] = useState<any>(null);

  // Brand Intelligence State
  const [brandInput, setBrandInput] = useState('');
  const [brandDomainInput, setBrandDomainInput] = useState('');
  const [brandIntelligence, setBrandIntelligence] = useState<any>(null);

  // Content Generation State
  const [contentTopic, setContentTopic] = useState('');
  const [generatedContent, setGeneratedContent] = useState<string>('');

  // Multi-Domain Comparison State
  const [domainsList, setDomainsList] = useState('');
  const [comparisonResults, setComparisonResults] = useState<any[]>([]);

  const analyzeDomain = async () => {
    if (!client || !domainInput.trim()) return;

    setLoading(true);
    try {
      console.log('🔄 Starting domain analysis for:', domainInput);
      const analysis = await client.getComprehensiveDomainAnalysis(domainInput.trim());
      console.log('✅ Domain analysis result:', analysis);
      setDomainAnalysis(analysis);
      setResults(prev => ({ ...prev, domain: analysis }));
      toast.success('Domain analysis complete!');
    } catch (error: any) {
      console.error('❌ Domain analysis error:', error);
      toast.error(`Analysis failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const analyzeBrand = async () => {
    if (!client || !brandInput.trim()) return;

    setLoading(true);
    try {
      console.log('🔄 Starting brand analysis for:', brandInput);
      const intelligence = await client.getBrandIntelligence(
        brandInput.trim(),
        brandDomainInput.trim() || undefined
      );
      console.log('✅ Brand intelligence result:', intelligence);
      setBrandIntelligence(intelligence);
      setResults(prev => ({ ...prev, brand: intelligence }));
      toast.success('Brand intelligence analysis complete!');
    } catch (error: any) {
      console.error('❌ Brand intelligence error:', error);
      toast.error(`Brand analysis failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const generateContent = async () => {
    if (!client || !contentTopic.trim()) return;

    setLoading(true);
    try {
      console.log('🔄 Starting content generation for:', contentTopic);
      const content = await client.generateContent(contentTopic.trim());
      console.log('✅ Content generation result:', content);
      setGeneratedContent(content);
      setResults(prev => ({ ...prev, content: content }));
      toast.success('Content generated successfully!');
    } catch (error: any) {
      console.error('❌ Content generation error:', error);
      toast.error(`Content generation failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const compareDomains = async () => {
    if (!client || !domainsList.trim()) return;

    setLoading(true);
    try {
      const domains = domainsList.split('\n').map(d => d.trim()).filter(d => d);
      console.log('🔄 Starting domain comparison for:', domains);
      const backlinks = await client.getBulkBacklinks(domains);

      console.log('✅ Domain comparison result:', backlinks);
      setComparisonResults(backlinks);
      setResults(prev => ({ ...prev, comparison: backlinks }));
      toast.success(`Compared ${backlinks?.length || 0} domains!`);
    } catch (error: any) {
      console.error('❌ Domain comparison error:', error);
      toast.error(`Domain comparison failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 size={32} className="text-blue-600" />
              SEO Intelligence Dashboard
            </h1>
            <p className="text-gray-600 mt-2">Unified DataForSEO intelligence platform - Domain analysis, brand monitoring, content generation, and competitor insights</p>
          </div>
          {client && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              <CheckCircle size={16} />
              API Connected
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('domain')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'domain'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Globe size={16} className="inline mr-2" />
              Domain Analysis
            </button>
            <button
              onClick={() => setActiveTab('brand')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'brand'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BrainIcon size={16} className="inline mr-2" />
              Brand Intelligence
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'content'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText size={16} className="inline mr-2" />
              Content Generation
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'comparison'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp size={16} className="inline mr-2" />
              Domain Comparison
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Domain Analysis Tab */}
          {activeTab === 'domain' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Domain Intelligence Analysis</h2>
              <p className="text-gray-600 mb-4">Comprehensive domain analysis combining backlinks, on-page SEO, technologies, and SERP positioning</p>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="example.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  className="input max-w-md inline-block mr-2"
                />
                <button
                  onClick={analyzeDomain}
                  disabled={!domainInput.trim() || loading}
                  className="btn-primary"
                >
                  {loading ? <RefreshIcon size={16} className="animate-spin inline mr-2" /> : <Search size={16} className="inline mr-2" />}
                  Analyze Domain
                </button>
              </div>

              {domainAnalysis && (
                <div className="space-y-6">
                  {domainAnalysis.backlinks && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Backlinks</p>
                            <p className="text-3xl font-bold text-green-600 mt-1">{domainAnalysis.backlinks.backlinks?.toLocaleString() || 'N/A'}</p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <Link className="text-green-600" size={24} />
                          </div>
                        </div>
                      </div>
                      <div className="card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Referring Domains</p>
                            <p className="text-3xl font-bold text-blue-600 mt-1">{domainAnalysis.backlinks.referring_domains?.toLocaleString() || 'N/A'}</p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="text-blue-600" size={24} />
                          </div>
                        </div>
                      </div>
                      <div className="card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Domain Rank</p>
                            <p className="text-3xl font-bold text-purple-600 mt-1">{domainAnalysis.backlinks.rank?.toLocaleString() || 'N/A'}</p>
                          </div>
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Star className="text-purple-600" size={24} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {domainAnalysis.keywords && domainAnalysis.keywords.length > 0 && (
                    <div className="card">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Ranking Keywords</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search Volume</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPC</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competition</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {domainAnalysis.keywords.slice(0, 10).map((kw: any, index: number) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{kw.keyword}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{kw.search_volume?.toLocaleString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">${kw.cpc}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    kw.competition_level === 'LOW' ? 'bg-green-100 text-green-800' :
                                    kw.competition_level === 'HIGH' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {kw.competition_level}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Brand Intelligence Tab */}
          {activeTab === 'brand' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Brand Intelligence Monitor</h2>
              <p className="text-gray-600 mb-4">Track brand mentions, content analysis, and review intelligence across the web</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Your brand name"
                  value={brandInput}
                  onChange={(e) => setBrandInput(e.target.value)}
                  className="input"
                />
                <input
                  type="text"
                  placeholder="yourbrand.com (optional)"
                  value={brandDomainInput}
                  onChange={(e) => setBrandDomainInput(e.target.value)}
                  className="input"
                />
              </div>
              <button
                onClick={analyzeBrand}
                disabled={!brandInput.trim() || loading}
                className="btn-primary"
              >
                {loading ? <RefreshIcon size={16} className="animate-spin inline mr-2" /> : <BrainIcon size={16} className="inline mr-2" />}
                Analyze Brand Intelligence
              </button>
            </div>
          )}

          {/* Content Generation Tab */}
          {activeTab === 'content' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">AI Content Generation</h2>
              <p className="text-gray-600 mb-4">Generate high-quality content, meta descriptions, and check grammar using AI</p>

              <div className="mb-4">
                <textarea
                  rows={4}
                  placeholder="Write a comprehensive guide about HVAC maintenance for homeowners..."
                  value={contentTopic}
                  onChange={(e) => setContentTopic(e.target.value)}
                  className="input w-full"
                />
              </div>
              <button
                onClick={generateContent}
                disabled={!contentTopic.trim() || loading}
                className="btn-primary"
              >
                {loading ? <RefreshIcon size={16} className="animate-spin inline mr-2" /> : <Sparkles size={16} className="inline mr-2" />}
                Generate Content
              </button>

              {generatedContent && (
                <div className="card mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Content</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">{generatedContent}</pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Domain Comparison Tab */}
          {activeTab === 'comparison' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Multi-Domain Comparison</h2>
              <p className="text-gray-600 mb-4">Compare backlink profiles and domain authority across multiple domains</p>

              <div className="mb-4">
                <textarea
                  rows={4}
                  placeholder="competitor1.com&#10;competitor2.com&#10;yourdomain.com"
                  value={domainsList}
                  onChange={(e) => setDomainsList(e.target.value)}
                  className="input w-full"
                />
              </div>
              <button
                onClick={compareDomains}
                disabled={!domainsList.trim() || loading}
                className="btn-primary"
              >
                {loading ? <RefreshIcon size={16} className="animate-spin inline mr-2" /> : <TrendingUp size={16} className="inline mr-2" />}
                Compare Domains
              </button>

              {comparisonResults && comparisonResults.length > 0 && (
                <div className="card mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Comparison Results</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Backlinks</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referring Domains</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain Rank</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {comparisonResults.map((result: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{result.target}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{result.backlinks?.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{result.referring_domains?.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{result.rank?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!client && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="text-yellow-400" size={24} />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">DataForSEO Not Configured</h3>
              <p className="mt-1 text-sm text-yellow-700">Please check your environment variables for DataForSEO API credentials.</p>
            </div>
          </div>
        </div>
      )}

      {client && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <CheckCircle className="text-green-400" size={24} />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">All DataForSEO APIs Integrated</h3>
              <p className="mt-1 text-sm text-green-700">Ready for comprehensive SEO intelligence gathering across 9 API families.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOIntelligencePage;
